/**
 * Converts a base64 string to an ArrayBuffer
 * @param base64 - The base64 encoded string
 * @returns ArrayBuffer containing the decoded data
 */
export function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes.buffer;
}

/**
 * Converts a base64 string to a File object
 * @param base64Data - The base64 encoded string (without data URI prefix)
 * @param fileName - The name for the file
 * @param mimeType - The MIME type of the file
 * @returns File object that can be uploaded
 */
export function base64ToFile(base64Data: string, fileName: string, mimeType: string): File {
  const arrayBuffer = base64ToArrayBuffer(base64Data);
  const blob = new Blob([arrayBuffer], { type: mimeType });
  return new File([blob], fileName, { type: mimeType });
}

/**
 * Prints a Blob object by creating a hidden iframe and invoking the print dialog
 * @param blob - The Blob object to be printed
 */
export function printBlob(blob: Blob) {
  const url = URL.createObjectURL(blob);

  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.visibility = 'hidden';

  iframe.src = url;
  document.body.appendChild(iframe);

  const cleanup = (revokeUrlDelayMs = 0) => {
    iframe.remove();

    if (revokeUrlDelayMs > 0) {
      setTimeout(() => URL.revokeObjectURL(url), revokeUrlDelayMs);
    } else {
      URL.revokeObjectURL(url);
    }

    window.removeEventListener('afterprint', handleAfterPrint);
  };

  const openInNewTabForManualPrint = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
    cleanup(60000);
  };

  const handleAfterPrint = () => {
    cleanup();
  };

  iframe.onload = () => {
    window.addEventListener('afterprint', handleAfterPrint);

    // IMPORTANT: delay slightly to allow render
    setTimeout(() => {
      const win = iframe.contentWindow;
      if (!win) {
        openInNewTabForManualPrint();
        return;
      }

      try {
        win.focus();
        win.print();
      } catch {
        openInNewTabForManualPrint();
      }
    }, 250);
  };
}

/**
 * Converts a base64 string to a Blob object
 * @param base64Data - The base64 encoded string (without data URI prefix)
 * @param mimeType - The MIME type of the file
 * @returns Blob object containing the decoded data
 */
export function base64ToBlob(base64Data: string, mimeType: string): Blob {
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}
